import { useEffect, useState } from "react";
import type { QuestionSetItem } from "../../../types/question-set";

const SCROLL_DELAY = 900; // must be higher than the animation time in <Expandable> component

const scrollToStep = (stepIndex: number) => {
	const el = document.getElementById(`question-set-${stepIndex}`);
	if (!el) return;
	setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), SCROLL_DELAY);
};

const findCompletedSteps = (questions: Array<QuestionSetItem>) =>
	questions
		.map((question, index) => [question, index] as const)
		.filter(([question]) => question.isCompleted)
		.map(([, index]) => index);

const useQuestionSetState = (questions: Array<QuestionSetItem>) => {
	const firstStep = 0;
	const allClosed = -1;

	const completedSteps = findCompletedSteps(questions);

	const [currentOpenStep, _setOpenStep] = useState(allClosed);

	const isStepDisabled = (step: number) =>
		step !== firstStep && // first step is always enabled
		!completedSteps.includes(step) && // a completed step is always enabled
		!completedSteps.includes(step - 1);

	const nextOpenStep =
		completedSteps.length !== 0
			? questions.every((q) => q.isCompleted)
				? allClosed
				: questions.findIndex((q) => !q.isCompleted)
			: firstStep;

	const setOpenStep = (stepIndex: number, shouldScroll = false) => {
		// close all questions if completing the last one, otherwise open next:
		_setOpenStep(stepIndex >= questions.length ? allClosed : stepIndex);
		if (!shouldScroll) return;

		// scroll to the question that was completed, if everything is complete:
		if (stepIndex === -1) scrollToStep(currentOpenStep);
		// else, scroll to the next incomplete step:
		else if (stepIndex > firstStep) scrollToStep(stepIndex);
	};

	useEffect(() => {
		// open next step when previous one completes
		setOpenStep(nextOpenStep, true);
	}, [completedSteps.length]);

	return {
		currentOpenStep,
		isStepDisabled,
		setOpenStep,
		nextOpenStep,
	};
};

export default useQuestionSetState;
